import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Field, useFormikContext } from 'formik';
import { Modal, Button, Form } from 'react-bootstrap';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons/faTrashAlt';

import Placeholder from 'components/Placeholder';
import FA from 'utils/FA';
import {
  customerGenderConfig as customerGenderConfigDuck,
  grades as gradesDuck,
  reviewCriteria as reviewCriteriaDuck
} from 'ducks';
import { duckify, filterDuck } from '../../../utils/duckify';
import { titleize } from '../../../utils';
import { ensureKeyedNoTotal } from '../../../utils/ensureKeyed';
import ReviewPage from './ReviewPage';
import './styles/RatingCriteria.scss';

const newRatingCategory = {
  name: '',
  scale: 1,
  labels: ['1'],
  applies_to: { school_level: null },
  weight: '1.00'
};

const normalizeResponse = response => ({ reviewCriteria: response });

const ConfirmCriteriaScaleChangeModal = ({
  show,
  handleCancel,
  handleSubmit
}) => {
  const [confirming, setConfirming] = useState(false);

  const { submitForm } = useFormikContext();

  const handleConfirm = () => {
    setConfirming(true);
    handleSubmit();
    submitForm();
  };

  return (
    <Modal
      show={show}
      animation={false}
      backdrop='static'
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Review Ratings Exist</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Ratings have already been entered by review readers for some Rating Categories.
        If you choose to proceed, previously entered ratings for these categories will be cleared.
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='secondary'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant='danger'
          onClick={handleConfirm}
          disabled={confirming}
        >
          {confirming ? 'Confirming...' : 'Confirm Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ConfirmCriteriaScaleChangeModal.propTypes = {
  /** Boolean to trigger modal display */
  show: PropTypes.bool.isRequired,

  /** action when Cancel is clicked */
  handleCancel: PropTypes.func.isRequired,

  /** action when Confirm is clicked */
  handleSubmit: PropTypes.func.isRequired
};

const CriteriaCard = ({
  criterion,
  genderOptions,
  schoolLevels,
  index,
  setItemPendingDeleteIndex,
  setShowConfirmDelete
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const { isSubmitting, setFieldValue } = useFormikContext();

  return (
    <div className='review-rating-criterion'>
      {criterion.errors && Object.keys(criterion.errors).length > 0 && (
        <div className='alert alert-danger'>
          <strong>There were errors processing this form.</strong>
          &nbsp;
          Please review the issues marked below:
          <ul>
            {Object.keys(criterion.errors).map(key => (
              criterion.errors[key].map(error => (
                <li>
                  {titleize(key)}
                  &nbsp;
                  {error}
                </li>
              ))
            )).flat()}
          </ul>
        </div>
      )}
      <Form.Group className='review-rating-criterion-row'>
        <Form.Label className='review-rating-criterion-label'>
          Rating Category
        </Form.Label>
        <div className='review-rating-criterion-input'>
          <Field
            as={Form.Control}
            size='sm'
            name={`reviewCriteria.${index}.name`}
            placeholder='Ex. "Academic Ability" or "Social Fit"'
            disabled={isSubmitting}
          />
        </div>
        <div className='review-rating-criterion-help'>
          <Button variant='link' onClick={() => setShowDetails(!showDetails)}>
            See Details&nbsp;
            <FA icon={showDetails ? faCaretDown : faCaretRight} nbsp />
          </Button>
        </div>
        {!showDetails && (
          <div className='review-rating-criterion-delete'>
            <Button
              variant='link'
              className='review-rating-criterion-delete-button'
              onClick={() => {
                setItemPendingDeleteIndex(index);
                setShowConfirmDelete(true);
              }}
              disabled={isSubmitting}
            >
              <FA icon={faTrashAlt} nbsp />
              Delete
            </Button>
          </div>
        )}
      </Form.Group>
      {showDetails && (
        <div className='review-rating-criterion-details'>
          <div className='review-rating-criterion-row'>
            <Form.Label className='review-rating-criterion-label'>
              Scoring Scale
            </Form.Label>
            <div className='review-rating-criterion-desc'>
              <div>
                Optionally label each value, or leave it blank to use a numeric scale. The systems scores from low (1)
                to high when averaged together on the Candidate Review page.
              </div>
              <div className='text-danger text-error'>
                <strong>Please note:&nbsp;</strong>
                If any readers have already entered scores for this rating category in the selected term,
                adding or removing values from the scoring scale below will clear their scores.
              </div>
            </div>
          </div>
          <div className='review-rating-criterion-scale'>
            {criterion.labels.map((label, labelIndex) => (
              <Form.Group className='review-rating-criterion-row review-rating-criterion-scale-item'>
                <Form.Label className='review-rating-criterion-label'>
                  {`${labelIndex + 1}=`}
                </Form.Label>
                <div className='review-rating-criterion-input'>
                  <Field
                    as={Form.Control}
                    size='sm'
                    name={`reviewCriteria.${index}.labels.${labelIndex}`}
                    placeholder='Ex. "Unacceptable", "Good", or "Exceptional"'
                    disabled={isSubmitting}
                  />
                </div>
                {criterion.labels.length > 1 && (
                  <div className='review-settings-item-remove'>
                    <Button
                      size='sm'
                      className='review-settings-item-remove-button'
                      variant='link'
                      disabled={isSubmitting}
                      onClick={() => {
                        setFieldValue(
                          `reviewCriteria.${index}.labels`,
                          criterion.labels.filter(val => label !== val)
                        );
                      }}
                    >
                      <FA icon={faTimesCircle} nbsp />
                    </Button>
                  </div>
                )}
                {labelIndex === 0 && (
                  <div className='review-rating-criterion-scale-text'>
                    (lowest)
                  </div>
                )}
              </Form.Group>
            )).map(ensureKeyedNoTotal('criterion_item'))}
            {criterion.labels.length < 10 && (
              <div className='review-rating-criterion-row'>
                <div className='review-rating-criterion-add-scale'>
                  <Button
                    variant='link'
                    className='review-settings-add-field'
                    onClick={() => {
                      setFieldValue(
                        `reviewCriteria.${index}.labels`,
                        [...criterion.labels, '']
                      );
                    }}
                    disabled={isSubmitting}
                  >
                    <FA icon={faPlusCircle} nbsp />
                    Add another
                  </Button>
                  (max 10)
                </div>
              </div>
            )}
          </div>
          <Form.Group className='review-rating-criterion-row'>
            <Form.Label className='review-rating-criterion-label'>
              Applicable School Levels
            </Form.Label>
            <div className='review-rating-criterion-input'>
              <Select
                multi
                value={criterion.applies_to.school_level}
                onChange={selected => (
                  setFieldValue(
                    `reviewCriteria.${index}.applies_to.school_level`,
                    selected.map(value => value.value)
                  )
                )}
                options={schoolLevels}
                placeholder='Select school levels, or leave blank to select all'
                disabled={isSubmitting}
              />
            </div>
          </Form.Group>
          <Form.Group className='review-rating-criterion-row'>
            <Form.Label className='review-rating-criterion-label'>
              Applicable Genders
            </Form.Label>
            <div className='review-rating-criterion-input'>
              <Select
                multi
                value={criterion.applies_to.gender}
                onChange={selected => (
                  setFieldValue(
                    `reviewCriteria.${index}.applies_to.gender`,
                    selected.map(value => value.value)
                  )
                )}
                options={genderOptions}
                placeholder='Leave blank to select all'
                disabled={isSubmitting}
              />
            </div>
          </Form.Group>
          <Form.Group className='review-rating-criterion-row'>
            <Form.Label className='review-rating-criterion-label'>
              Score Weight
            </Form.Label>
            <div className='review-rating-criterion-input'>
              <Field
                as={Form.Control}
                size='sm'
                placeholder='1.00'
                name={`reviewCriteria.${index}.weight`}
                disabled={isSubmitting}
              />
              <Form.Text>
                The weight determines how much the criteria is factored into the final score.
                Examples: 0 means it is removed from the scoring. 0.5 means the criteria
                contributes at 50% of the standard value. 2 would mean the score counts twice.
              </Form.Text>
            </div>
          </Form.Group>
          <div className='review-rating-criterion-row'>
            <div className='review-rating-criterion-delete'>
              <Button
                variant='link'
                className='review-rating-criterion-delete-button'
                onClick={() => {
                  setItemPendingDeleteIndex(index);
                  setShowConfirmDelete(true);
                }}
                disabled={isSubmitting}
              >
                <FA icon={faTrashAlt} nbsp />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CriteriaCard.propTypes = {
  /** Gender options for Applicable Genders select */
  genderOptions: PropTypes.arrayOf(PropTypes.shape({})),

  /** Gender options for Applicable Genders select */
  schoolLevels: PropTypes.arrayOf(PropTypes.shape({})),

  /** Criterion data for card */
  criterion: PropTypes.shape({
    id: PropTypes.string,
    labels: PropTypes.arrayOf(PropTypes.string),
    applies_to: PropTypes.object,
    errors: PropTypes.object
  }),

  /** Index of current card */
  index: PropTypes.number,

  /** Sets the index of the criteria to be deleted in parent state */
  setItemPendingDeleteIndex: PropTypes.func.isRequired,

  /** Sets the parent state to display the confirm delete modal */
  setShowConfirmDelete: PropTypes.func.isRequired
};

CriteriaCard.defaultProps = {
  genderOptions: [],
  schoolLevels: [],
  criterion: {
    id: '',
    labels: [],
    applies_to: {}
  },
  index: 0
};

const CriteriaCardContainer = ({
  schoolLevels,
  setItemPendingDeleteIndex,
  genderConfig,
  setShowConfirmDelete
}) => {
  const {
    values,
    isSubmitting,
    setFieldValue
  } = useFormikContext();

  const getGenders = () => {
    const genderMap = genderConfig.gender_map;
    return genderConfig.gender_options.map(option => ({
      value: option,
      label: genderMap[option]
    }));
  };

  return (
    <React.Fragment>
      {values.reviewCriteria.map((criterion, index) => (
        <CriteriaCard
          key={criterion.id}
          genderOptions={getGenders()}
          schoolLevels={schoolLevels}
          setItemPendingDeleteIndex={setItemPendingDeleteIndex}
          setShowConfirmDelete={setShowConfirmDelete}
          criterion={criterion}
          index={index}
        />
      )).map(ensureKeyedNoTotal('criteria_card'))}
      <Button
        variant='link'
        className='review-settings-add-field'
        onClick={() => (
          setFieldValue(
            'reviewCriteria',
            [
              ...values.reviewCriteria,
              newRatingCategory
            ]
          ))}
        disabled={isSubmitting}
      >
        <FA icon={faPlusCircle} nbsp />
        Add a Rating Category
      </Button>
    </React.Fragment>
  );
};

CriteriaCardContainer.propTypes = {
  /** Array of Applicable School Levels */
  schoolLevels: PropTypes.arrayOf(PropTypes.shape({})),

  /** Callback to set the item pending delete hwen delete is selected */
  setItemPendingDeleteIndex: PropTypes.func,

  /** Callback to display the confirm delete modal */
  setShowConfirmDelete: PropTypes.func
};

CriteriaCardContainer.defaultProps = {
  customerGenderOptions: {},
  schoolLevels: [],
  setItemPendingDeleteIndex: () => false,
  setShowConfirmDelete: () => false
};

const RatingCriteria = ({
  className,
  reviewCriteria,
  customerGenderConfig,
  grades,
  route,
  allDucksReady,
  schoolYearId,
  saveReviewCriteria,
  deleteReviewCriteria,
  checkRatings,
  reloadDucks
}) => {
  const [formikValues, setFormikValues] = useState({ reviewCriteria });
  const [itemPendingDeleteIndex, setItemPendingDeleteIndex] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmScaleChangeModal, setShowConfirmScaleChangeModal] = useState(false);
  const [confirmOverrwrite, setConfirmOverrwrite] = useState(false);

  const handleConfirmScaleChangeCancel = () => setShowConfirmScaleChangeModal(false);
  const handleConfirmScaleChangeConfirm = () => {
    setShowConfirmScaleChangeModal(false);
    setConfirmOverrwrite(true);
  };

  /** Initialize Formik state and update to ducks when year is changed */
  useEffect(() => {
    if (allDucksReady) {
      if (reviewCriteria.length > 0) {
        setFormikValues({ reviewCriteria });
      } else {
        setFormikValues({ reviewCriteria: [newRatingCategory] });
      }
    }
  }, [allDucksReady, schoolYearId]);

  const getSchoolLevels = () => (
    grades
      .map(grade => ({
        value: grade.school_level,
        label: grade.school_level
      }))
      .filter((grade, index, self) => (
        index === self.findIndex(obj => (
          obj.value === grade.value
        ))
      ))
  );

  const criteriaScaleChanged = criteria => (
    criteria.filter(criterion => {
      if (criterion.id) {
        return criterion.scale !== criterion.labels.length;
      }
      return false;
    })
  );

  const onSubmit = ({ values, formikContext }) => {
    const { setFieldValue } = formikContext;
    const criteriasScaleChangedArray = criteriaScaleChanged(values.reviewCriteria);
    if (criteriasScaleChangedArray.length > 0 && !confirmOverrwrite) {
      const ids = criteriasScaleChangedArray.map(criterion => criterion.id);
      return checkRatings(ids)
        .then(res => {
          if (res.total > 0) {
            setShowConfirmScaleChangeModal(true);
            return Promise.resolve({ skipFlash: true });
          }
          return saveReviewCriteria({
            reviewCriteria: values.reviewCriteria,
            id: schoolYearId
          })
            .catch(json => {
              if (json.response.status === 422) {
                setFieldValue('reviewCriteria', json.review_criteria);
              }
              return Promise.reject(json);
            });
        });
    }
    return saveReviewCriteria({
      reviewCriteria: values.reviewCriteria,
      id: schoolYearId
    })
      .catch(json => {
        if (json.response.status === 422) {
          const respArray = json.review_criteria.map(criterion => {
            if (criterion.name === null) {
              return { ...criterion, name: '' };
            }
            return criterion;
          });
          setFieldValue('reviewCriteria', respArray);
        }
        return Promise.reject(json);
      })
      .then(() => {
        setConfirmOverrwrite(false);
      });
  };

  const handleDelete = (formikContext) => {
    const { values, setFieldValue } = formikContext;
    const { id } = values.reviewCriteria[itemPendingDeleteIndex];
    const array = [...values.reviewCriteria];

    array.splice(itemPendingDeleteIndex, 1);
    setShowConfirmDelete(false);

    /** If item needs to be deleted from server db send delete */
    if (id) {
      return (
        deleteReviewCriteria({ id })
          .then(() => setFieldValue('reviewCriteria', array))
          .then(() => setFormikValues({
            reviewCriteria: array
          }))
      );
    }

    setFieldValue('reviewCriteria', array);
    return Promise.resolve({ skipFlash: true });
  };

  return (
    <ReviewPage
      className={className}
      header='Rating Criteria'
      headerHelperText={`
        Define the criteria by which your readers will evaluate candidates, and the scoring scale they will use.
      `}
      initialFormikValues={formikValues}
      route={route}
      showConfirmDelete={showConfirmDelete}
      onSubmit={onSubmit}
      onConfirm={handleDelete}
      onClose={() => setShowConfirmDelete(false)}
      reloadDucks={reloadDucks}
    >
      <ConfirmCriteriaScaleChangeModal
        show={showConfirmScaleChangeModal}
        handleCancel={handleConfirmScaleChangeCancel}
        handleSubmit={handleConfirmScaleChangeConfirm}
      />
      <Placeholder.loading
        ready={allDucksReady}
        content={() => (
          <CriteriaCardContainer
            schoolLevels={getSchoolLevels()}
            genderConfig={customerGenderConfig}
            setItemPendingDeleteIndex={setItemPendingDeleteIndex}
            setShowConfirmDelete={setShowConfirmDelete}
          />
        )}
      />
    </ReviewPage>
  );
};

RatingCriteria.propTypes = {
  /** gender information from current customer */
  customerGenderConfig: PropTypes.object,
  /** Classname prop */
  className: PropTypes.string,

  /** Current Track Silo Selected School Id */
  schoolYearId: PropTypes.string.isRequired,

  /** Review Criteria Duck */
  reviewCriteria: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    weight: PropTypes.string,
    scale: PropTypes.number,
    labels: PropTypes.arrayOf(PropTypes.string),
    applies_to: PropTypes.shape({
      school_levels: PropTypes.arrayOf(PropTypes.string),
      gender: PropTypes.arrayOf(PropTypes.string)
    })
  })).isRequired,

  /** Current Customer */
  customer: PropTypes.shape({}).isRequired,

  /** Review Criteria Duck */
  grades: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    school_level: PropTypes.string
  })).isRequired,

  /** React Router Route object */
  route: PropTypes.shape({}).isRequired,

  /** AllDucksReady boolean */
  allDucksReady: PropTypes.bool.isRequired,

  /** Submit Ducks Function */
  saveReviewCriteria: PropTypes.func.isRequired,

  /** Delete Ducks Function */
  deleteReviewCriteria: PropTypes.func.isRequired,

  /** Check Ratings Duck */
  checkRatings: PropTypes.func.isRequired,

  /** marks ducks dirty for reload */
  reloadDucks: PropTypes.func.isRequired
};

RatingCriteria.defaultProps = {
  className: ''
};

const mapDucksToProps = props => {
  const { schoolYearId } = props;
  return {
    grades: gradesDuck,
    customerGenderConfig: customerGenderConfigDuck,
    reviewCriteria: filterDuck(reviewCriteriaDuck, { school_year_id: schoolYearId })
  };
};

const mapDispatchToProps = dispatch => ({
  saveReviewCriteria: data => {
    const reviewCriteria = data.reviewCriteria.map(criterion => ({
      ...criterion,
      scale: criterion.labels.length
    }));
    return (
      dispatch(reviewCriteriaDuck.actions.bulkUpdate(reviewCriteria, { school_year_id: data.id }))
        .then(reviewCriteriaDuck.actions.denormalizeObjects)
        .then(normalizeResponse)
    );
  },
  deleteReviewCriteria: criterion => (
    dispatch(reviewCriteriaDuck.actions.remove(criterion))
      .then(reviewCriteriaDuck.actions.denormalizeObjects)
  ),
  checkRatings: ids => (
    dispatch(reviewCriteriaDuck.actions.checkRatings(ids))
      .then(reviewCriteriaDuck.actions.denormalizeObjects)
  ),
  reloadDucks: () => dispatch(reviewCriteriaDuck.actions.markDirty())
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(RatingCriteria);
